import { Building2, CreditCard, Crosshair, FileCheck, Landmark, MapPin, Network, ShieldAlert, Users } from 'lucide-react';

/*
 * Content for the Financial Services scenario walkthrough, drawn from the
 * `financial_services` industry demo pack in the BOSSS repo.
 *
 * Halcyon National Bank is a composite demo organization, not a real customer.
 * Every `artifact` below is a verbatim record title from the pack, with two
 * exceptions whose source records carry no title field and whose labels are
 * therefore descriptive: the Millbrook contract (scenario 3) and the nightly
 * core-banking backup (scenario 4).
 *
 * The framing rules this content must satisfy — no invented results, no banned
 * outcome adjectives, no SLA claim on scenario 1 — are enforced by
 * halcyon.test.js. Fix the copy, not the test.
 */

export const halcyonScenario = {
  org: {
    name: 'Halcyon National Bank, N.A.',
    subtitle: 'Five situations a bank actually faces, and the records BOSSS produces for each.',
    framingNote: 'a composite organization in the BOSSS demo environment',

    // Rendered as context cards above the carousel — this is the org every
    // scenario below happens to, so each entry has to read standalone.
    profile: [
      { icon: Users, text: '~850 employees' },
      { icon: MapPin, text: '12 branches across Minnesota' },
      { icon: Landmark, text: 'OCC-supervised national bank' },
      { icon: Network, text: 'Halcyon Pay LLC subsidiary' },
    ],
  },
  scenarios: [
    {
      id: 'wire-fraud',
      number: 1,
      title: 'Wire-Fraud Attempt',
      tagline: 'A phished treasury analyst and an out-of-pattern SWIFT instruction',
      icon: ShieldAlert,
      frameworks: ['BSA/AML', 'GLBA', 'NIST CSF 2.0'],
      situation: {
        narrative: 'A treasury analyst on the wire desk receives a phishing email impersonating a SWIFT vendor '
          + 'support ticket, and enters his credentials on a cloned identity-provider login page. Two days later '
          + 'the attacker submits an international wire through the SWIFT Alliance Gateway — large amount, new '
          + 'beneficiary bank, outside his normal approval pattern.',
        cast: [
          { name: 'Brian Nakamura', role: 'Treasury Analyst, wire desk' },
          { name: 'Elena Vasquez', role: 'Chief Information Security Officer' },
          { name: 'Patricia Nguyen', role: 'BSA Officer' },
          { name: 'Ryan Halvorsen', role: 'SOC Analyst, Tier 2 lead' },
        ],
      },
      timing: [
        { at: '09:30', label: 'First event' },
        { at: '10:20', label: 'Alert acknowledged' },
        { at: '10:30', label: 'Investigation opened' },
        { at: '+2 days', label: 'Case closed — wire held before settlement' },
      ],
      response: [
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Correlates endpoint, SIEM and identity telemetry into a critical alert at 97% confidence',
          artifact: 'Out-of-pattern SWIFT wire instruction — new beneficiary, above threshold',
        },
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'SOC escalates to an investigation case; workstation isolated and credentials reset',
          artifact: 'Wire Fraud Investigation — Compromised Treasury Credentials',
        },
        {
          module: 'Service Desk',
          href: '/platform/service-desk',
          action: 'Ops-side incident ticket opens for the operational response',
          artifact: 'Out-of-pattern SWIFT wire instruction flagged by SOC — suspected wire fraud',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Formal incident record, with an elevated entry on the risk register',
          artifact: 'Wire Fraud via Compromised Treasury Credentials — SWIFT Gateway',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The control gap becomes a funded, critical POA&M with a due date',
          artifact: 'POA&M — Dual-Control Gap for Above-Threshold New-Beneficiary Wires',
        },
        {
          module: 'Documents',
          href: '/platform/documents',
          action: 'Board reporting closes the loop',
          artifact: 'Q2 Board Security Report: Wire Fraud Incident',
        },
      ],
      enables: [
        'The SOC analyst, the BSA officer and the general counsel all work one incident record — no export, no re-keying, no reconciliation meeting.',
        'The POA&M links back to the finding that produced it, and forward to the actions that close it.',
      ],
      impact: {
        entities: [
          { name: 'Halcyon National Bank, N.A.', detail: 'Treasury Operations', location: 'Minneapolis, MN' },
        ],
        locations: [
          { name: 'Halcyon National Bank HQ', detail: 'Headquarters · Mark Oyelaran', location: 'Minneapolis, MN' },
        ],
        applications: [
          { name: 'SWIFT Alliance Gateway', location: 'Minneapolis, MN' },
          { name: 'Treasury Workstation — Brian Nakamura', detail: 'Compromised', location: 'Minneapolis, MN' },
          { name: 'CoreLedger 9', detail: 'Core banking platform', location: 'Minneapolis, MN' },
          { name: 'Northlight Identity SSO', detail: 'Cloned login page', location: 'Minneapolis, MN' },
        ],
        vendors: [
          { name: 'Ansett Financial Messaging', detail: 'SWIFT gateway', location: 'Jersey City, NJ' },
          { name: 'Northlight Identity', detail: 'SSO / identity provider', location: 'San Francisco, CA' },
          { name: 'Fieldstone Cyber Defense', detail: 'Endpoint detection', location: 'Austin, TX' },
          { name: 'Beacon Security Analytics', detail: 'SIEM', location: 'Reston, VA' },
        ],
        data: [
          { name: 'Wire Transfer Instructions', detail: 'Data set', location: 'Minneapolis, MN' },
          { name: 'Wire Beneficiary Account Number', detail: '~1,800 wires/year', location: 'Minneapolis, MN' },
          { name: 'Wire Transfer Amount', detail: 'Confidential', location: 'Minneapolis, MN' },
          { name: 'SWIFT Alliance Gateway to SWIFT Network', detail: 'Transmission', location: 'Minneapolis, MN → SWIFT network' },
        ],
      },
    },
    {
      id: 'pci-program',
      number: 2,
      title: 'PCI DSS v4.0 Program',
      tagline: 'An in-flight program with open gaps and a QSA date on the calendar',
      icon: CreditCard,
      frameworks: ['PCI DSS v4.0', 'NIST CSF 2.0'],
      situation: {
        narrative: 'A PCI DSS v4.0 program sits at roughly 70% control-instance implementation across the '
          + 'card-management platform, the ATM fleet and online-banking card payments. Two requirement gaps are '
          + 'open, evidence collection is under way, and a QSA on-site assessment is scheduled.',
        cast: [
          { name: 'Sophia Delacroix', role: 'GRC Analyst' },
          { name: 'Priyanka Rao', role: 'Application Security Engineer' },
        ],
      },
      timing: [
        { at: '~70%', label: 'Control-instance implementation' },
        { at: '+45 days', label: 'QSA assessment window opens' },
      ],
      response: [
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Program activation tracks every control instance to implementation state',
          artifact: 'PCI DSS v4.0 Compliance Program',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Open remediation finding against cardholder data at rest',
          artifact: 'PCI DSS Req 3.5.1 — Cardholder Data at Rest Not Rendered Unreadable',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Open remediation finding against administrative access to the CDE',
          artifact: 'PCI DSS Req 8.4.2 — MFA Not Enforced for All CDE Admin Access',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Segmentation evidence attaches to the requirement it satisfies',
          artifact: 'Q1 2026 PCI Network Segmentation Penetration Test Summary',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'A certification project tracks the QSA engagement to its assessment window',
          artifact: 'PCI DSS v4.0 QSA Certification — Halcyon National Bank',
        },
      ],
      enables: [
        'Control implementation state, open findings, collected evidence and the QSA engagement are one program view rather than four spreadsheets.',
        'Segmentation test evidence attaches to the requirement it satisfies, so an assessor follows a link instead of searching a folder.',
      ],
      impact: {
        entities: [
          { name: 'Halcyon National Bank, N.A.', detail: 'Cardholder data environment', location: 'Minneapolis, MN' },
        ],
        locations: [
          { name: 'Halcyon Operations Center', detail: 'Office · Mark Oyelaran', location: 'Bloomington, MN' },
          { name: 'Downtown Minneapolis Branch', detail: 'Branch Office · Frank Delgado', location: 'Minneapolis, MN' },
          { name: 'St. Paul Branch', detail: 'Branch Office · Heather Nystrom', location: 'St. Paul, MN' },
          { name: 'Rochester Branch', detail: 'Branch Office · Victor Amadi', location: 'Rochester, MN' },
        ],
        applications: [
          { name: 'Continental Card Management Platform', location: 'Bloomington, MN' },
          { name: 'ATM fleet', detail: '12 branches', location: 'Minnesota' },
          { name: 'NorthBridge Connect', detail: 'Online and mobile banking', location: 'Minneapolis, MN' },
        ],
        vendors: [
          { name: 'Continental Card Processing', detail: 'Card issuer processing', location: 'Wilmington, DE' },
          { name: 'Ironclad ATM Solutions', detail: 'ATM hardware and servicing', location: 'Columbus, OH' },
          { name: 'Northbridge Digital Banking', detail: 'Online banking platform', location: 'Kansas City, MO' },
          { name: 'Larkspur Assurance Partners', detail: 'QSA', location: 'Chicago, IL' },
        ],
        data: [
          { name: 'Cardholder Data Set', detail: 'PCI classification', location: 'Minneapolis, MN' },
          { name: 'Card Primary Account Number (PAN)', detail: '~35,000 active cards', location: 'Minneapolis, MN' },
          { name: 'Card CVV/CVC', detail: 'PCI classification', location: 'Minneapolis, MN' },
          { name: 'Card Management to Card Network Authorization', detail: 'Transmission', location: 'Bloomington, MN → card network' },
        ],
      },
    },
    {
      id: 'vendor-breach',
      number: 3,
      title: 'Statement-Vendor Breach',
      tagline: 'A third party discloses a breach — nine days past its contractual window',
      icon: Building2,
      frameworks: ['GLBA', 'NIST CSF 2.0'],
      situation: {
        narrative: 'The statement print and mail vendor discloses a breach of its mail-house environment, with '
          + 'bank statement data in scope. Internal discovery predated the vendor\'s formal notification by nine '
          + 'days, exceeding the contractual notification window.',
        cast: [
          { name: 'Gregory Simmons', role: 'Vendor Risk Analyst' },
          { name: 'Renee Okafor', role: 'General Counsel' },
        ],
      },
      timing: [
        { at: '9 days', label: 'Internal discovery preceded vendor notification' },
        { at: '−28 days', label: 'Re-assessment package opened' },
        { at: '+30 days', label: 'Closeout targeted' },
      ],
      response: [
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'An out-of-cycle due-diligence package opens against the vendor',
          artifact: 'Millbrook Statement Services — Breach Re-Assessment',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The security questionnaire is re-issued; encryption at rest comes back "no"',
          artifact: 'Millbrook Statement Services — Post-Breach Security Re-Assessment',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'That answer becomes a logged exception with an owner and a review date',
          artifact: 'Accepted Risk — Millbrook Statement Services Encryption-at-Rest Gap Pending Remediation',
        },
        {
          module: 'Business',
          href: '/platform/business',
          action: 'The contract record surfaces the governing clauses — encrypted SFTP as the only authorized transfer, secure destruction of print waste within five business days, no subcontracting without written approval',
          artifact: 'Millbrook print and mail master services agreement',
        },
        {
          module: 'Documents',
          href: '/platform/documents',
          action: 'Due-diligence findings issue as a report',
          artifact: 'Millbrook Statement Services Due-Diligence Re-Assessment Report',
        },
      ],
      enables: [
        'A questionnaire answer becomes a tracked exception with an owner and a date, not a note in someone\'s inbox.',
        'The contract sits beside the assessment, so a notification window is checkable at the moment it is missed rather than at renewal.',
      ],
      impact: {
        entities: [
          { name: 'Halcyon National Bank, N.A.', detail: 'Retail banking', location: 'Minneapolis, MN' },
          { name: 'Halcyon Pay LLC', detail: 'Second due-diligence package' },
        ],
        locations: [
          { name: 'Halcyon National Bank HQ', detail: 'Headquarters · statement extract origin', location: 'Minneapolis, MN' },
          { name: 'Millbrook mail-house facility', detail: 'Vendor site — breached', location: 'Milwaukee, WI' },
        ],
        applications: [
          { name: 'CoreLedger 9', detail: 'Statement extract source', location: 'Minneapolis, MN' },
        ],
        vendors: [
          { name: 'Millbrook Statement Services', detail: 'Breached — statement print and mail', location: 'Milwaukee, WI' },
          { name: 'Trailhead Payment Rails', detail: 'Routine annual review', location: 'San Francisco, CA' },
        ],
        data: [
          { name: 'Quarterly Customer Statement Package', detail: 'Data collection', location: 'Minneapolis, MN' },
          { name: 'Customer Home Address', detail: '~48,000 records', location: 'Minneapolis, MN' },
          { name: 'Bank Account Number', detail: '~62,000 active accounts', location: 'Minneapolis, MN' },
          { name: 'CoreLedger to Millbrook Statement Print Extract', detail: 'Transmission', location: 'Minneapolis, MN → Milwaukee, WI' },
        ],
      },
    },
    {
      id: 'ransomware-tabletop',
      number: 4,
      title: 'Red Team and Ransomware Tabletop',
      tagline: 'The exercise that found the gap before the attacker did',
      icon: Crosshair,
      frameworks: ['NIST CSF 2.0', 'FFIEC CAT'],
      situation: {
        narrative: 'A full-scope red-team exercise models the same attack path as scenario one — phish, '
          + 'compromised treasury workstation, SWIFT reconnaissance. It found a local-administrator credential '
          + 'reused across the whole treasury workstation fleet, allowing lateral movement to a SWIFT-adjacent '
          + 'segment, and no dual-control step on large or new-beneficiary wires. That is the same control gap '
          + 'the real attempt later exploited. A tabletop then exercises the continuity and recovery plans '
          + 'against a ransomware variant of the same premise.',
        cast: [
          { name: 'Jordan Whitaker', role: 'SOC Manager' },
          { name: 'William Hartley', role: 'IT Director' },
        ],
      },
      timing: [
        { at: '−65 to −58 days', label: 'Red-team exercise ran' },
        { at: '−55 days', label: 'Report delivered' },
        { at: '−60 days', label: 'Tabletop exercise ran' },
        { at: '−30 days', label: 'Retest' },
      ],
      response: [
        {
          module: 'Pentest',
          href: '/platform/pentest',
          action: 'Red-team engagement spanning phishing, physical, internal network and wireless phases',
          artifact: '2026 Red Team Exercise — Treasury Wire-Desk Attack-Path Simulation',
        },
        {
          module: 'Pentest',
          href: '/platform/pentest',
          action: 'The finding that anticipated the live incident',
          artifact: 'Lateral Movement from Phished Treasury Workstation to SWIFT-Adjacent Network Segment',
        },
        {
          module: 'BCRP',
          href: '/platform/resilience',
          action: 'Continuity plan scoped to wire transfer, ACH origination, treasury management and online banking',
          artifact: 'Core Banking & Wire Operations Ransomware Continuity Plan',
        },
        {
          module: 'BCRP',
          href: '/platform/resilience',
          action: 'Recovery plan for the core platform and the SWIFT gateway',
          artifact: 'Core Banking & SWIFT Gateway Disaster Recovery Plan',
        },
        {
          module: 'CMDB',
          href: '/platform/cmdb',
          action: 'Backup posture is inventory rather than assumption — nightly full, automated, cross-region object storage, AES-256',
          artifact: 'Nightly core-banking backup',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The exercise is recorded as a control test, with results and a next-test date',
          artifact: 'Ransomware Tabletop Exercise 2026',
        },
      ],
      enables: [
        'A red-team finding and a live incident can reference the same control gap — which is exactly how this exercise turned out to have anticipated the real attempt.',
        'The continuity plan, the recovery plan and the backup that underwrites them are linked records rather than three documents with three owners.',
      ],
      impact: {
        entities: [
          { name: 'Halcyon National Bank, N.A.', detail: 'Treasury Operations, IT', location: 'Minneapolis, MN' },
        ],
        locations: [
          { name: 'Halcyon National Bank HQ', detail: 'Headquarters · Mark Oyelaran', location: 'Minneapolis, MN' },
          { name: 'Halcyon DR/Backup Site', detail: 'Disaster Recovery Site · William Hartley', location: 'Eagan, MN' },
        ],
        applications: [
          { name: 'Treasury workstation fleet', detail: 'Shared local-admin credential', location: 'Minneapolis, MN' },
          { name: 'SWIFT Alliance Gateway', detail: 'Lateral-movement target', location: 'Minneapolis, MN' },
          { name: 'Core Banking Server — Primary', location: 'Minneapolis, MN' },
          { name: 'Core Banking Server — DR Failover', location: 'Eagan, MN' },
          { name: 'HQ Perimeter Firewall', location: 'Minneapolis, MN' },
        ],
        vendors: [
          { name: 'Fieldstone Cyber Defense', detail: 'Endpoint detection', location: 'Austin, TX' },
          { name: 'Beacon Security Analytics', detail: 'SIEM', location: 'Reston, VA' },
        ],
        data: [
          { name: 'Wire Transfer Instructions', detail: 'Data set', location: 'Minneapolis, MN' },
          { name: 'Account Master', detail: '~62,000 active accounts', location: 'Minneapolis, MN' },
          { name: 'Nightly core-banking backup', detail: 'Cross-region, AES-256', location: 'Eagan, MN' },
        ],
      },
    },
    {
      id: 'glba-audit',
      number: 5,
      title: 'GLBA 501(b) Audit',
      tagline: 'The annual safeguards audit, with its evidence already live',
      icon: FileCheck,
      frameworks: ['GLBA', 'SOX', 'NIST CSF 2.0'],
      situation: {
        narrative: 'The annual Safeguards Rule audit of the written information security program: access '
          + 'controls, encryption, vendor oversight and incident response all reviewed and re-attested, assessed '
          + 'formally against the authority, with the quarterly access review standing as cited evidence.',
        cast: [
          { name: 'Patricia Nguyen', role: 'BSA Officer' },
          { name: 'Robert Sundqvist', role: 'Compliance Director' },
          { name: 'Renee Okafor', role: 'General Counsel' },
        ],
      },
      timing: [
        { at: '−90 to −10 days', label: 'Assessment window' },
        { at: 'Open now', label: 'Access review campaign' },
        { at: '+35 days', label: 'Campaign deadline' },
      ],
      response: [
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The program policy sits under governance with review and attestation history',
          artifact: 'GLBA Information Security Program / WISP',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'A formal assessment runs against the authority itself',
          artifact: 'GLBA Section 501(b) Safeguards Rule — Annual Information Security Program Assessment (2026)',
        },
        {
          module: 'IAM',
          href: '/platform/iam',
          action: 'Quarterly recertification across core banking, SWIFT, online banking and card management — the evidence the audit cites',
          artifact: 'Q2 2026 Quarterly Access Review — Halcyon National Bank Core Systems',
        },
      ],
      enables: [
        'The access review the audit cites is a live campaign record, not a screenshot pasted into an appendix.',
        'Policy attestation history, the assessment and its evidence all resolve to one authority.',
      ],
      impact: {
        entities: [
          { name: 'Halcyon National Bank, N.A.', detail: 'Whole-institution scope', location: 'Minneapolis, MN' },
          { name: 'Halcyon Pay LLC', detail: 'Subsidiary in scope' },
        ],
        locations: [
          { name: 'Halcyon National Bank HQ', detail: 'Headquarters · Mark Oyelaran', location: 'Minneapolis, MN' },
          { name: 'Halcyon Operations Center', detail: 'Office · Mark Oyelaran', location: 'Bloomington, MN' },
          { name: 'Halcyon DR/Backup Site', detail: 'Disaster Recovery Site · William Hartley', location: 'Eagan, MN' },
        ],
        applications: [
          { name: 'CoreLedger 9', detail: 'Core banking', location: 'Minneapolis, MN' },
          { name: 'SWIFT Alliance Gateway', detail: 'Wire messaging', location: 'Minneapolis, MN' },
          { name: 'NorthBridge Connect', detail: 'Online and mobile banking', location: 'Minneapolis, MN' },
          { name: 'Continental Card Management Platform', location: 'Bloomington, MN' },
          { name: 'Northlight Identity SSO', detail: 'Access recertification', location: 'Minneapolis, MN' },
        ],
        vendors: [
          { name: 'Larkspur Assurance Partners', detail: 'Independent assessor', location: 'Chicago, IL' },
        ],
        data: [
          { name: 'Customer PII Profile', detail: 'Data set', location: 'Minneapolis, MN' },
          { name: 'Customer Social Security Number', detail: 'PII classification', location: 'Minneapolis, MN' },
          { name: 'Account Master', detail: '~62,000 active accounts', location: 'Minneapolis, MN' },
        ],
      },
    },
  ],
};
